import { Box, Skeleton } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import { THUMBNAIL_ASPECT_RATIO } from '../../../helpers/content-image'
import PromotedContentCardContent from './card-content'
import PromotedCardContainer from './container'
import usePromotedCardMethods, { PROMOTED_CARD_TYPES } from './use-promoted-card-methods'

const useStyles = makeStyles(({ spacing, typography, radius }) => ({
  root: {
    height: '100%'
  },
  container: {
    width: '100%',
    alignItems: 'flex-start',
    paddingTop: spacing(2)
  },
  // PENG-3630: 16:9, the ratio editors upload and crop at, so the thumbnail
  // shows the whole image (it was 107x80 and cut ~13% off each side).
  imageContanier: {
    width: '107px',
    aspectRatio: THUMBNAIL_ASPECT_RATIO,
    marginLeft: spacing(2),
    flexShrink: 0
  },
  image: {
    display: 'block',
    height: '100%',
    width: '100%',
    borderRadius: radius.lg,
    objectFit: 'cover'
  },
  contentContainer: {
    // stretch, not height 100%: a 100% height ignores the bottom margin
    alignSelf: 'stretch',
    justifyContent: 'flex-start',
    paddingBottom: 0,
    paddingTop: 0,
    flex: 1,
    minWidth: 0,
    '&&': {
      // The shared calc() width pushed this Box 8px past the card's right
      // edge (clipping "Add to Playlist"); flex sizing fits it instead. The
      // margin keeps the hover tint off the card's edges and the image, and
      // the side padding shrinks to match so the text doesn't move.
      width: 'auto',
      margin: spacing(0, 1, 1, 1),
      paddingLeft: spacing(1),
      paddingRight: spacing(1)
    }
  },
  // `&&` doubles the class selector (`.cardTitle.cardTitle`), bumping
  // specificity above card-content.js's base `cardTitle` class. Without
  // this, base + override classes have equal specificity and source order
  // wins — which goes to the base because parent useStyles is inserted
  // before child useStyles in the React render tree.
  cardTitle: {
    '&&': {
      ...typography.heading.h6
    }
  },
  cardSummary: {
    '&&': {
      ...typography.body.s,
      fontWeight: typography.weight.regular,
      lineClamp: 2,
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  },
  cardContentType: {
    ...typography.body.s
  },
  // No pill here: next to the thumbnail the meta row is too narrow for its
  // padding, and the read time ran into the card's edge. Plain icon + label
  // keeps the type readable and hands those pixels back to the time; the
  // right margin stops the two touching on the tightest cards.
  cardContentTypeRoot: {
    '&&': {
      backgroundColor: 'transparent',
      padding: 0,
      marginRight: spacing(1)
    }
  },
  actions: {
    marginTop: 'auto',
    // Plus the content Box's 8px bottom margin, the actions sit 16px off the
    // card's bottom, matching the 16px above the image.
    marginBottom: spacing(1)
  },
  time: {
    ...typography.body.s
  }
}))

const PromotedContentGenericCard = ({ content, onSelect, position, testId, size, onAddToPlaylist, ...props }) => {
  const classes = useStyles()
  const {
    contentImage,
    handleCardSelect,
    cardSize,
    hoverShadow,
    actions
  } = usePromotedCardMethods({ content, position, size, cardType: PROMOTED_CARD_TYPES.GENERIC, onSelect, onAddToPlaylist })

  const handleImageKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleCardSelect()
    }
  }

  return (
    <PromotedCardContainer
      size={cardSize}
      hoverShadow={hoverShadow}
      actions={actions}
      content={content}
      testId={testId}
    >
      <Box display='flex' flexDirection='row' className={classes.container}>
        <Box
          className={classes.imageContanier}
          onClick={handleCardSelect}
          onKeyDown={handleImageKeyDown}
          role='button'
          tabIndex={0}
          aria-label={content ? `View ${content.title}` : 'Loading content'}
          sx={{
            cursor: 'pointer',
            '&:focus': {
              outline: '2px solid #1976d2',
              outlineOffset: '2px',
              borderRadius: '8px'
            }
          }}
        >
          {
              content
                ? (
                  <img
                    src={contentImage}
                    title={content?.image?.altText}
                    className={classes.image}
                    data-test={`card-image-${content?.friendlyUrl}`}
                    alt={content?.image?.altText || ''}
                  />
                  )
                : (
                  <Skeleton variant='rectangular' className={classes.image} />
                  )
            }
        </Box>
        <PromotedContentCardContent
          content={content}
          actions={actions}
          cardType={PROMOTED_CARD_TYPES.GENERIC}
          defaultSectionMargin={1}
          onSelect={handleCardSelect}
          overrides={{
            contentContainer: classes.contentContainer,
            cardTitle: classes.cardTitle,
            cardSummary: classes.cardSummary,
            cardContentType: classes.cardContentTypeRoot,
            cardContentTypeText: classes.cardContentType,
            actions: classes.actions,
            time: classes.time
          }}
        />
      </Box>
    </PromotedCardContainer>
  )
}

export default PromotedContentGenericCard
